import { __decorate, __metadata } from 'tslib';
import SchematicUIService from '../../controllers/base/schematic-ui-service.js';
import { $logger } from '../../controllers/logger/logger.js';
import { Service } from '../../decorators/class/service.js';
import { ServiceNames } from '../../constants/service-name.js';
import { BuiltinUiState } from '../store/builtin-state.js';
import { StateManager } from '../store/state-manager.js';
import { BehaviorSubject } from 'rxjs';
import { debounceTime } from 'rxjs/operators';
import { TransitionService } from '../transition/transition-service.js';
import { WorkbenchConfigurationState } from '../settings/auto-sync-settings.js';
import { isGroupConfig, isGroupSchema } from './utils.js';

const reservedTitlebarItems = [
    { name: 'divider', content: null, priority: Infinity },
    { name: 'pusher', content: null, priority: Infinity },
];
const reserved = reservedTitlebarItems.map(item => item.name);
/**
 * 标题栏 UI 服务
 *
 * @public
 */
let TitlebarService = class TitlebarService extends SchematicUIService {
    constructor(store, transitionService) {
        super();
        this.store = store;
        this.transitionService = transitionService;
        this.componentsWidth = new Map();
        this.calculatedWidth = new Map();
        this.calculatedVisible = new Map();
        this.widthBeforeSetMax = new Map();
        this.totalWidth = new BehaviorSubject(0);
        this.containerWidth = 0;
        this.schema = [];
        this.store.register(BuiltinUiState.Titlebar, []);
        this.store.register(BuiltinUiState.TitlebarCalculatedWidth, {});
        this.store.register(BuiltinUiState.TitlebarShow, true);
        this.store.register(BuiltinUiState.TitlebarHover, false);
        this.store.register(BuiltinUiState.TitlebarListenMouseMove, false);
        this.store.register(WorkbenchConfigurationState.DisableTitlebar, false).subscribe((value) => {
            this.store.upsert(BuiltinUiState.TitlebarDOMVisible, !value);
        });
        reservedTitlebarItems.forEach((config) => {
            this.configs.set(config.name, config);
        });
        this.totalWidth.pipe(debounceTime(this.debounceTime)).subscribe(this.calculate.bind(this));
        this.transitionService.register(ServiceNames.TITLEBAR, {
            duration: 150,
            entered: {},
            entering: {
                transform: 'translateY(0)',
                transition: 'all linear 0.15s',
            },
            exited: {
                position: 'fixed',
                transform: 'translateY(-100%)',
                height: 0,
            },
            exiting: {
                height: 0,
                transform: 'translateY(-100%)',
                transition: 'all linear 0.15s',
            },
        });
    }
    updateWidth(config, clientWidth, outerWidth) {
        // pathMap 用 config 作为 key，存在内存泄漏的隐患，使用时注意 clear
        super.onUpdate();
        this.componentsWidth.set(config.name, [clientWidth, outerWidth]);
        // 如果ui变更，设置maxWidth对宽度影响的重新计数
        this.resetLastTimeWidth(config.name);
        const configs = this.reduceSchema();
        const totalWidth = configs.reduce((acc, cur) => {
            const width = this.componentsWidth.get(cur.name);
            if (!width) {
                return acc;
            }
            return acc + width[0] + width[1];
        }, 0);
        this.totalWidth.next(totalWidth);
    }
    updateContainerWidth(width) {
        this.containerWidth = width;
        this.calculate();
    }
    $forceUpdate() {
        this.forceUpdateWithStore(this.store, BuiltinUiState.Titlebar);
    }
    isContinuousShrinkComponent(config) {
        return config.shrinkable === 'continuous' && config.widthRange !== undefined;
    }
    isStepsShrinkComponent(config) {
        var _a, _b;
        return config.shrinkable === 'steps' && ((_b = (_a = config.shrinkSteps) === null || _a === void 0 ? void 0 : _a.length) !== null && _b !== void 0 ? _b : 0) > 0;
    }
    /**
     * 是否更新maxWidth
     * 展开时，Continuous类型会持续更新maxWidth，
     * 当发现maxWidth对实际宽度没有影响，就不在更新这一项的maxWidth， 以便下一个优先级的能展开
     */
    needToUpdateMaxWidth(name) {
        const lastWidthInfo = this.widthBeforeSetMax.get(name);
        if (!lastWidthInfo) {
            return true;
        }
        const { width, count } = lastWidthInfo;
        // 由于监听titlebar宽度是debounce的，可能两次监听titlebar宽度变化中间
        // 没有更新item的实际宽度, 所以加个计数，如果超过三次真实宽度还是没有改变，证明maxWidth对宽度无影响
        if (count <= 3) {
            lastWidthInfo.count += 1;
            return true;
        }
        const currentWidth = this.componentsWidth.get(name);
        const oldWidth = width;
        if (oldWidth[0] !== (currentWidth === null || currentWidth === void 0 ? void 0 : currentWidth[0]) || oldWidth[1] !== (currentWidth === null || currentWidth === void 0 ? void 0 : currentWidth[1])) {
            return true;
        }
        return false;
    }
    updateLastWidthToCurrent(name) {
        var _a, _b, _c;
        this.widthBeforeSetMax.set(name, {
            width: (_a = this.componentsWidth.get(name)) !== null && _a !== void 0 ? _a : [0, 0],
            count: (_c = (_b = this.widthBeforeSetMax.get(name)) === null || _b === void 0 ? void 0 : _b.count) !== null && _c !== void 0 ? _c : 0,
        });
    }
    /**
     * 由于监听titlebar宽度是debounce的，可能两次监听titlebar宽度变化中间
     * 没有更新item的实际宽度
     */
    resetLastTimeWidth(name) {
        const lastWidthInfo = this.widthBeforeSetMax.get(name);
        if (lastWidthInfo) {
            lastWidthInfo.count = 0;
        }
    }
    expand(totalWidth) {
        const configs = this.getConfigMapDividedByGroup();
        if (this.calculatedWidth.size > 0 || this.calculatedVisible.size > 0) {
            // 将隐藏的组件展示出来
            const widthAfterExpand = [...this.calculatedVisible.entries()]
                .filter(([, value]) => value === false)
                .map(([key]) => configs.get(key))
                .sort((a, b) => { var _a, _b; return ((_a = b.priority) !== null && _a !== void 0 ? _a : 0) - ((_b = a.priority) !== null && _b !== void 0 ? _b : 0); })
                .reduce((currentTotalWidth, config) => {
                var _a;
                if (currentTotalWidth === this.containerWidth) {
                    return currentTotalWidth;
                }
                const [clientWidth, outerWidth] = (_a = this.componentsWidth.get(config.name)) !== null && _a !== void 0 ? _a : [0, 0];
                const lastReportWidth = clientWidth + outerWidth;
                if (currentTotalWidth + lastReportWidth <= this.containerWidth) {
                    this.calculatedVisible.delete(config.name);
                    return currentTotalWidth + lastReportWidth;
                }
                return this.containerWidth;
            }, totalWidth);
            // 将压缩的组件扩展
            [...this.calculatedWidth.keys()]
                .map(key => configs.get(key))
                .sort((a, b) => { var _a, _b; return ((_a = b.priority) !== null && _a !== void 0 ? _a : 0) - ((_b = a.priority) !== null && _b !== void 0 ? _b : 0); })
                .reduce((currentTotalWidth, config) => {
                var _a, _b, _c;
                if (currentTotalWidth === this.containerWidth) {
                    return currentTotalWidth;
                }
                if (this.isContinuousShrinkComponent(config)) {
                    if (!this.needToUpdateMaxWidth(config.name)) {
                        return currentTotalWidth;
                    }
                    const [, max] = config.widthRange;
                    const [currentWidth] = (_a = this.componentsWidth.get(config.name)) !== null && _a !== void 0 ? _a : [0, 0];
                    const maxExpandableWidth = max - currentWidth;
                    this.updateLastWidthToCurrent(config.name);
                    if (currentTotalWidth + maxExpandableWidth > this.containerWidth) {
                        this.calculatedWidth.set(config.name, this.containerWidth - currentTotalWidth + currentWidth);
                        return this.containerWidth;
                    }
                    this.calculatedWidth.delete(config.name);
                    return currentTotalWidth + maxExpandableWidth;
                }
                if (this.isStepsShrinkComponent(config)) {
                    const [minWidth] = config.shrinkSteps;
                    const maxWidth = config.shrinkSteps.slice(-1).pop();
                    const [currentWidth] = (_b = this.componentsWidth.get(config.name)) !== null && _b !== void 0 ? _b : [0, 0];
                    const expandTo = (_c = [...config.shrinkSteps].filter((widthStep) => {
                        const expandableWidth = widthStep - currentWidth;
                        if (currentTotalWidth + expandableWidth <= this.containerWidth) {
                            return true;
                        }
                        return false;
                    }).pop()) !== null && _c !== void 0 ? _c : minWidth;
                    const expandableWidth = currentWidth - expandTo;
                    if (expandableWidth === 0 && maxWidth !== expandTo) {
                        // 避免低优先级组件越级 expand，在这里通过返回容器宽度停止迭代
                        return this.containerWidth;
                    }
                    this.calculatedWidth.set(config.name, expandTo);
                    return currentTotalWidth + expandableWidth;
                }
                return currentTotalWidth;
            }, widthAfterExpand);
        }
        this.deliver();
    }
    shrink(totalWidth) {
        const configs = this.getConfigMapDividedByGroup();
        const cachedCalculatedWidth = new Map([...this.calculatedWidth.entries()]);
        this.calculatedWidth.clear();
        this.calculatedVisible.clear();
        const widthAfterShrink = [...configs.values()]
            .filter(config => config.shrinkable)
            .sort((a, b) => { var _a, _b; return ((_a = a.priority) !== null && _a !== void 0 ? _a : 0) - ((_b = b.priority) !== null && _b !== void 0 ? _b : 0); })
            .reduce((currentTotalWidth, config) => {
            var _a, _b, _c;
            if (currentTotalWidth <= this.containerWidth) {
                if (cachedCalculatedWidth.has(config.name)) {
                    this.calculatedWidth.set(config.name, cachedCalculatedWidth.get(config.name));
                }
                return currentTotalWidth;
            }
            if (this.isContinuousShrinkComponent(config)) {
                const [min] = config.widthRange;
                const [currentWidth] = (_a = this.componentsWidth.get(config.name)) !== null && _a !== void 0 ? _a : [0, 0];
                const maxShrinkableWidth = currentWidth - min;
                if (currentTotalWidth - maxShrinkableWidth <= this.containerWidth) {
                    this.calculatedWidth.set(config.name, currentWidth - (currentTotalWidth - this.containerWidth));
                    return this.containerWidth;
                }
                this.calculatedWidth.set(config.name, min);
                return currentTotalWidth - maxShrinkableWidth;
            }
            if (this.isStepsShrinkComponent(config)) {
                const [minWidth] = config.shrinkSteps;
                const [currentWidth] = (_b = this.componentsWidth.get(config.name)) !== null && _b !== void 0 ? _b : [0, 0];
                const shrinkTo = (_c = [...config.shrinkSteps].reverse().find((widthStep) => {
                    const shrinkableWidth = currentWidth - widthStep;
                    if (currentTotalWidth - shrinkableWidth <= this.containerWidth) {
                        return true;
                    }
                    return false;
                })) !== null && _c !== void 0 ? _c : minWidth;
                const shrinkableWidth = currentWidth - shrinkTo;
                this.calculatedWidth.set(config.name, shrinkTo);
                return currentTotalWidth - shrinkableWidth;
            }
            return currentTotalWidth;
        }, totalWidth);
        [...configs.values()]
            .sort((a, b) => { var _a, _b; return ((_a = a.priority) !== null && _a !== void 0 ? _a : 0) - ((_b = b.priority) !== null && _b !== void 0 ? _b : 0); })
            .reduce((currentTotalWidth, config) => {
            var _a, _b;
            if (currentTotalWidth <= this.containerWidth || this.calculatedVisible.get(config.name) === false
                || config.preventHide === true) {
                return currentTotalWidth;
            }
            const [clientWidth, outerWidth] = (_a = this.componentsWidth.get(config.name)) !== null && _a !== void 0 ? _a : [0, 0];
            const currentWidth = (_b = this.calculatedWidth.get(config.name)) !== null && _b !== void 0 ? _b : clientWidth;
            const fullWidth = currentWidth + outerWidth;
            this.calculatedVisible.set(config.name, false);
            return currentTotalWidth - fullWidth;
        }, widthAfterShrink);
        this.deliver();
    }
    calculate() {
        const totalWidth = [...this.calculatedVisible.entries()].reduce((acc, [key, visible]) => {
            var _a;
            if (visible === false) {
                const [clientWidth, outerWidth] = (_a = this.componentsWidth.get(key)) !== null && _a !== void 0 ? _a : [0, 0];
                return acc - clientWidth - outerWidth;
            }
            return acc;
        }, this.totalWidth.getValue());
        if (this.containerWidth === 0 || this.containerWidth >= totalWidth) {
            this.expand(totalWidth);
            return;
        }
        this.shrink(this.totalWidth.getValue());
    }
    deliver() {
        const widthMap = {};
        this.getConfigMapDividedByGroup().forEach((config) => {
            var _a;
            const maxWidth = this.getMaxWidth(config);
            const visible = (_a = this.calculatedVisible.get(config.name)) !== null && _a !== void 0 ? _a : true;
            widthMap[config.name] = { width: maxWidth, visible };
        });
        this.store.update(BuiltinUiState.TitlebarCalculatedWidth, widthMap);
    }
    convertTitlebar(configs) {
        const titlebar = [];
        let tempContainer = [];
        configs.forEach((config, index) => {
            if (!isGroupConfig(config) && config.name === 'divider') {
                titlebar.push(tempContainer);
                tempContainer = [];
            }
            else {
                tempContainer.push(config);
                if (index === configs.length - 1) {
                    titlebar.push(tempContainer);
                }
            }
        });
        return titlebar;
    }
    onUpdate() {
        super.onUpdate();
        const config = this.convertTitlebar(this.reduceSchema());
        this.store.get(BuiltinUiState.Titlebar).next(config);
    }
    reduceSchema() {
        return this.schema.reduce(this.readSchema.bind(this), []);
    }
    // 以group为单位的configs
    getConfigMapDividedByGroup() {
        const configs = this.reduceSchema();
        const result = new Map();
        configs.forEach(item => result.set(item.name, item));
        return result;
    }
    getMaxWidth(config) {
        var _a, _b;
        if (this.isContinuousShrinkComponent(config)) {
            return (_a = this.calculatedWidth.get(config.name)) !== null && _a !== void 0 ? _a : config.widthRange[1];
        }
        if (this.isStepsShrinkComponent(config)) {
            return (_b = this.calculatedWidth.get(config.name)) !== null && _b !== void 0 ? _b : config.shrinkSteps.slice(-1).pop();
        }
        return undefined;
    }
    readSchema(root, item) {
        if (!isGroupSchema(item)) {
            const config = this.get(item);
            root.push(config);
            this.setPathToActionInfoInConfig(config);
            return root;
        }
        // group组件要遍历子按钮
        const multiLineConfig = this.get(item.key);
        const rows = item.items
            .map(rowItems => rowItems.map((subItem) => {
            const subConfig = Object.assign(Object.assign({}, this.get(subItem)), { shrinkable: 'continuous', widthRange: undefined });
            this.setPathToActionInfoInConfig(subConfig);
            return subConfig;
        }))
            .filter(row => !!row.length)
            .map(row => this.convertTitlebar(row));
        const config = Object.assign(Object.assign({}, multiLineConfig), { name: item.key, rows });
        if (config.rows.length) {
            root.push(config);
        }
        return root;
    }
    get(name) {
        return super.get(name, {
            content: null,
            name: 'none',
        });
    }
    mergeSchemaWithoutUpdate(schema) {
        this.schema = this.schema.concat(schema);
    }
    /**
     * 向标题栏管理器注册标题栏组件
     *
     * @param config - 组件配置
     * @param updateImmediately - 是否立刻更新
     */
    register(config, updateImmediately = true) {
        if (reserved.includes(config.name)) {
            $logger.warn(`The titlebar config "${config.name}" is reserved.`);
            return;
        }
        super.register(config, updateImmediately);
    }
    /**
     * 隐藏标题栏
     */
    hide() {
        this.store.update(BuiltinUiState.TitlebarShow, false);
    }
    /**
     * 显示标题栏
     */
    show() {
        this.store.update(BuiltinUiState.TitlebarShow, true);
    }
    /**
     * 开关标题栏
     */
    toggle() {
        this.store.update(BuiltinUiState.TitlebarShow, !this.isShowing());
    }
    /**
     * 当前标题栏是否展示
     */
    isShowing() {
        return !!this.store.getValue(BuiltinUiState.TitlebarShow);
    }
    /**
     * 进入hover模式
     */
    enterHover() {
        this.setHoverActive(true);
        this.store.update(BuiltinUiState.TitlebarHover, true);
    }
    /**
    * 退出hover模式
    */
    exitHover() {
        this.setHoverActive(false);
        this.store.update(BuiltinUiState.TitlebarHover, false);
    }
    /**
    * 设置是否通过监听mousemove关闭titlebar
    */
    setHoverActive(value) {
        this.store.update(BuiltinUiState.TitlebarListenMouseMove, value);
    }
    /**
     * 设置标题栏激活状态
     * @param active - 激活状态
     */
    setActive(active) {
        this.store.update(BuiltinUiState.TitlebarFocus, active);
    }
};
TitlebarService = __decorate([
    Service({ name: ServiceNames.TITLEBAR }),
    __metadata("design:paramtypes", [StateManager, TransitionService])
], TitlebarService);

export { TitlebarService };
//# sourceMappingURL=titlebar-service.js.map
